import React from 'react';
import {
  Box,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Markdown,
  Select,
  Text,
} from '@chatsdet/ui';
import { Check, Copy, Download } from '@phosphor-icons/react';
import { buildTestSignInSetupPrompt, TEST_SIGN_IN_STACKS, type TestSignInStack } from '@chatsdet/types';
import { copyTextToClipboard } from '../clipboard-copy.js';

const STACK_OPTIONS = TEST_SIGN_IN_STACKS.map((s) => ({ id: s.id, label: s.label }));

type Props = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  appName: string;
  issuer: string;
  clientId: string;
  redirectUris: string[];
  emailDomain: string;
};

/**
 * Renders `buildTestSignInSetupPrompt` VERBATIM — its wording is a security
 * control (email-domain check, existing-users-only, staging-only), so this
 * dialog must never paraphrase or trim it. Copy/Download hand off the exact
 * markdown string; the on-screen render is for legibility only.
 */
export function TestSignInSetupPromptDialog({
  isOpen,
  onOpenChange,
  appName,
  issuer,
  clientId,
  redirectUris,
  emailDomain,
}: Props) {
  const [stack, setStack] = React.useState<TestSignInStack>('generic');
  const [copied, setCopied] = React.useState(false);

  const prompt = React.useMemo(
    () => buildTestSignInSetupPrompt({ stack, appName, issuer, clientId, redirectUris, emailDomain }),
    [stack, appName, issuer, clientId, redirectUris, emailDomain]
  );

  const handleCopy = async () => {
    const ok = await copyTextToClipboard(prompt);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([prompt], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cofactor-test-sign-in-${appName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <DialogTrigger size="4xl" isOpen={isOpen} onOpenChange={onOpenChange}>
      <span />
      <Dialog title="Setup instructions">
        <DialogContent>
          <Box className="space-y-4 min-w-0 max-w-full">
            <Box className="min-w-0 max-w-full">
              <Select
                label="Stack"
                items={STACK_OPTIONS}
                selectedKey={stack}
                onSelectionChange={(key) => setStack(key as TestSignInStack)}
              />
            </Box>
            <Box className="min-w-0 max-w-full rounded-lg bg-muted p-4 max-h-[50vh] overflow-y-auto overflow-x-hidden">
              <Markdown
                className={cn(
                  'prose prose-sm max-w-none text-foreground break-words',
                  '[&_code]:break-words [&_code]:[overflow-wrap:anywhere]',
                  '[&_a]:break-words [&_a]:[overflow-wrap:anywhere]',
                  '[&_pre]:whitespace-pre-wrap [&_pre]:[overflow-wrap:anywhere]'
                )}
              >
                {prompt}
              </Markdown>
            </Box>
            <Text size="xs" className="text-muted-foreground">
              Hand this to your engineer or coding agent as-is. It carries the security
              requirements your app needs to follow.
            </Text>
          </Box>
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" iconLeft={<Download size={14} />} onPress={handleDownload}>
            Download .md
          </Button>
          <Button
            variant="primary"
            iconLeft={copied ? <Check size={14} /> : <Copy size={14} />}
            onPress={handleCopy}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
